<div class="w-full" [formGroup]="proximityForm">
  <div class="flex flex-col lg:flex-row gap-2 items-stretch">

    <!-- Address (free-form, takes remaining horizontal space) -->
    <div class="lg:flex-1 lg:min-w-0">
      <div class="flex items-center rounded-md bg-white px-3 outline outline-1 -outline-offset-1 outline-gray-300 focus-within:outline focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-blue-royal h-[36px] dark:bg-secondary dark:outline-secondary"
           [class.!outline-red-500]="required && proximityForm.get('address')?.invalid && proximityForm.get('address')?.touched">
        <input
          type="text"
          formControlName="address"
          placeholder="City, postcode, or full address"
          class="block min-w-0 grow border-0 bg-transparent py-1.5 pl-1 pr-3 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0 focus:border-0 focus:shadow-none sm:text-sm sm:leading-6 dark:text-white dark:placeholder:text-gray-500"
        />
      </div>
    </div>

    <!-- Radius + unit combined: number input on the left, custom unit dropdown on the right -->
    <div class="lg:w-32 lg:shrink-0">
      <div class="flex items-center rounded-md bg-white pl-3 pr-1 outline outline-1 -outline-offset-1 outline-gray-300 focus-within:outline focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-blue-royal h-[36px] dark:bg-secondary dark:outline-secondary"
           [class.!outline-red-500]="required && proximityForm.get('radius')?.invalid && proximityForm.get('radius')?.touched">
        <input
          type="text"
          inputmode="decimal"
          [value]="radiusDisplayValue"
          (input)="onRadiusInput($event)"
          placeholder="Radius"
          [attr.aria-valuemin]="0"
          [attr.aria-valuemax]="maxForCurrentUnit()"
          class="block w-full min-w-0 grow border-0 bg-transparent py-1.5 pl-0 pr-1 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0 focus:border-0 focus:shadow-none sm:text-sm sm:leading-6 dark:text-white dark:placeholder:text-gray-500"
        />
        <div class="relative shrink-0">
          <button
            type="button"
            (click)="toggleUnitDropdown($event)"
            [attr.aria-expanded]="unitDropdownOpen"
            class="flex items-center px-1 text-sm text-gray-500 dark:text-gray-400 focus:outline-none"
          >
            <span>{{ proximityForm.value.unit }}</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                 [class.rotate-180]="unitDropdownOpen"
                 class="ml-0.5 text-gray-400 hover:text-gray-600 transition-transform duration-200">
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>
          <div *ngIf="unitDropdownOpen"
               class="absolute right-0 z-10 mt-1 w-24 overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 sm:text-sm dark:bg-secondary-800">
            <div *ngFor="let opt of UNIT_OPTIONS"
                 (click)="selectUnit(opt.value)"
                 class="relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-900 hover:bg-blue-royal group dark:text-white">
              <span class="block truncate group-hover:text-white">{{ opt.label }}</span>
              <span *ngIf="proximityForm.value.unit === opt.value"
                    class="absolute inset-y-0 right-0 flex items-center pr-2 text-blue-royal group-hover:text-white">
                <svg class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                </svg>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Strictness -->
    <div class="lg:w-44 lg:shrink-0">
      <div class="relative">
        <button
          type="button"
          (click)="toggleStrictnessDropdown($event)"
          [attr.aria-expanded]="strictnessDropdownOpen"
          class="w-full flex items-center justify-between rounded-md bg-white px-3 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-blue-royal h-[36px] text-sm text-gray-900 dark:bg-secondary dark:outline-secondary dark:text-white"
        >
          <span class="block truncate">{{ strictnessLabel(proximityForm.value.strictness) }}</span>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
               [class.rotate-180]="strictnessDropdownOpen"
               class="ml-2 text-gray-400 hover:text-gray-600 transition-transform duration-200 shrink-0">
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </button>
        <div *ngIf="strictnessDropdownOpen"
             class="absolute z-10 mt-1 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 sm:text-sm dark:bg-secondary-800">
          <div *ngFor="let opt of STRICTNESS_OPTIONS"
               (click)="selectStrictness(opt.value)"
               class="relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-900 hover:bg-blue-royal group dark:text-white">
            <span class="block truncate group-hover:text-white">{{ opt.label }}</span>
            <span *ngIf="proximityForm.value.strictness === opt.value"
                  class="absolute inset-y-0 right-0 flex items-center pr-2 text-blue-royal group-hover:text-white">
              <svg class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
              </svg>
            </span>
          </div>
        </div>
      </div>
    </div>

  </div>
</div>
